import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useT } from '../i18n';

type ConfirmOptions = {
  title: string;
  description: string;
  confirmLabel?: string;
  tone?: 'default' | 'danger';
};

type PendingConfirm = ConfirmOptions & {
  resolve: (value: boolean) => void;
};

type PromptOptions = {
  title: string;
  initialValue?: string;
  confirmLabel: string;
};

type PendingPrompt = PromptOptions & {
  value: string;
  resolve: (value: string | null) => void;
};

export function useConfirmDialog() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const t = useT();

  const confirm = (options: ConfirmOptions) =>
    new Promise<boolean>((resolve) => {
      setPending({ ...options, resolve });
    });

  const close = (result: boolean) => {
    const current = pending;
    if (!current) return;
    setPending(null);
    current.resolve(result);
  };

  const confirmDialog = (
    <Dialog.Root open={pending !== null} onOpenChange={(open) => !open && close(false)}>
      <Dialog.Portal>
        <Dialog.Overlay className="nv-confirm-overlay" />
        <Dialog.Content
          className="nv-root nv-confirm"
          data-tone={pending?.tone === 'danger' ? 'danger' : 'default'}
        >
          <div className="nv-confirm-head">
            {pending?.tone === 'danger' && (
              <span className="nv-confirm-icon" aria-hidden="true">
                <svg
                  role="presentation"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v5M14 11v5" />
                </svg>
              </span>
            )}
            <Dialog.Title className="nv-confirm-title">{pending?.title}</Dialog.Title>
          </div>
          <Dialog.Description className="nv-confirm-text">
            {pending?.description}
          </Dialog.Description>
          <div className="nv-confirm-actions">
            <button type="button" className="btn-secondary" onClick={() => close(false)}>
              {t('cancel')}
            </button>
            <button
              type="button"
              className={pending?.tone === 'danger' ? 'btn-danger-primary' : 'btn-primary'}
              onClick={() => close(true)}
            >
              {pending?.confirmLabel ?? t('delete')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );

  return { confirm, confirmDialog };
}

export function usePromptDialog() {
  const [pending, setPending] = useState<PendingPrompt | null>(null);
  const t = useT();

  const prompt = (options: PromptOptions) =>
    new Promise<string | null>((resolve) => {
      setPending({ ...options, value: options.initialValue ?? '', resolve });
    });

  const close = (result: string | null) => {
    const current = pending;
    if (!current) return;
    setPending(null);
    current.resolve(result);
  };

  const promptDialog = (
    <Dialog.Root open={pending !== null} onOpenChange={(open) => !open && close(null)}>
      <Dialog.Portal>
        <Dialog.Overlay className="nv-confirm-overlay" />
        <Dialog.Content className="nv-root nv-confirm">
          <div className="nv-confirm-head">
            <Dialog.Title className="nv-confirm-title">{pending?.title}</Dialog.Title>
          </div>
          <input
            className="input nv-confirm-input"
            value={pending?.value ?? ''}
            onChange={(event) =>
              setPending((current) =>
                current ? { ...current, value: event.target.value } : current,
              )
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const next = pending?.value.trim();
                close(next ? next : null);
              }
            }}
          />
          <div className="nv-confirm-actions">
            <button type="button" className="btn-secondary" onClick={() => close(null)}>
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                const next = pending?.value.trim();
                close(next ? next : null);
              }}
            >
              {pending?.confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );

  return { prompt, promptDialog };
}
